'use client'

/**
 * Browser half of progress sync: a tiny external store (localStorage-first)
 * that React reads through useSyncExternalStore, plus a sync loop that merges
 * with the server copy whenever this device has a sync key.
 */

import { useSyncExternalStore } from 'react'
import {
  EMPTY_PROGRESS,
  dayKey,
  emptyProgress,
  generateSyncKey,
  hasMeaningfulProgress,
  mergeProgress,
  normalizeSyncKey,
  sameProgress,
  sanitizeProgress,
  type ProgressState,
} from './progress'

const STATE_KEY = 'gd:progress:v1'
const SYNC_KEY = 'gd:sync-key'
const ENDPOINT = '/api/gradient/progress'
const SYNC_DEBOUNCE_MS = 2500

export type SyncStatus = 'local' | 'syncing' | 'synced' | 'offline' | 'error'

export type SyncInfo = {
  key: string | null
  status: SyncStatus
  lastSyncedAt: number | null
  message: string | null
}

type Snapshot = { state: ProgressState; sync: SyncInfo }

const SERVER_SNAPSHOT: Snapshot = {
  state: EMPTY_PROGRESS,
  sync: { key: null, status: 'local', lastSyncedAt: null, message: null },
}

// ─── Store ─────────────────────────────────────────────────────────────────────

let snapshot: Snapshot | null = null
const listeners = new Set<() => void>()
let syncTimer: ReturnType<typeof setTimeout> | null = null
let inflight: Promise<void> | null = null
let dirty = false
let booted = false

function safeGet(key: string): string | null {
  try {
    return window.localStorage.getItem(key)
  } catch {
    return null
  }
}

function safeSet(key: string, value: string | null) {
  try {
    if (value === null) window.localStorage.removeItem(key)
    else window.localStorage.setItem(key, value)
  } catch {
    /* private mode or quota — progress still lives in memory this session */
  }
}

function readLocal(): Snapshot {
  let state = emptyProgress()
  const raw = safeGet(STATE_KEY)
  if (raw) {
    try {
      state = sanitizeProgress(JSON.parse(raw))
    } catch {
      /* corrupt — start clean rather than crash */
    }
  }
  const key = normalizeSyncKey(safeGet(SYNC_KEY) ?? '')
  return { state, sync: { key, status: key ? 'syncing' : 'local', lastSyncedAt: null, message: null } }
}

function current(): Snapshot {
  if (!snapshot) snapshot = readLocal()
  return snapshot
}

function emit() {
  for (const l of listeners) l()
}

function setState(next: ProgressState, opts: { markDirty: boolean }) {
  const snap = current()
  if (sameProgress(snap.state, next)) return
  snapshot = { ...snap, state: next }
  safeSet(STATE_KEY, JSON.stringify(next))
  if (opts.markDirty) {
    dirty = true
    scheduleSync()
  }
  emit()
}

function setSync(patch: Partial<SyncInfo>) {
  const snap = current()
  snapshot = { ...snap, sync: { ...snap.sync, ...patch } }
  emit()
}

function update(mutator: (draft: ProgressState) => void) {
  const base = current().state
  const draft: ProgressState = {
    ...base,
    steps: { ...base.steps },
    done: { ...base.done },
    solved: { ...base.solved },
    code: { ...base.code },
    days: { ...base.days },
  }
  mutator(draft)
  const now = Date.now()
  draft.updatedAt = now
  const today = dayKey()
  if (!(today in draft.days)) draft.days[today] = now
  setState(draft, { markDirty: true })
}

// ─── Sync loop ─────────────────────────────────────────────────────────────────

function randomBytes(n: number): Uint8Array {
  const out = new Uint8Array(n)
  crypto.getRandomValues(out)
  return out
}

function scheduleSync(delay = SYNC_DEBOUNCE_MS) {
  if (typeof window === 'undefined') return
  if (syncTimer) clearTimeout(syncTimer)
  syncTimer = setTimeout(() => {
    syncTimer = null
    void syncNow()
  }, delay)
}

async function request(method: 'GET' | 'PUT' | 'DELETE', key: string, body?: unknown, keepalive = false) {
  return fetch(ENDPOINT, {
    method,
    headers: { 'x-gradient-key': key, ...(body ? { 'content-type': 'application/json' } : {}) },
    body: body ? JSON.stringify(body) : undefined,
    keepalive,
    cache: 'no-store',
  })
}

/** Push local changes (creating a sync key on first meaningful progress) and pull the merge back. */
export async function syncNow(opts: { keepalive?: boolean } = {}): Promise<void> {
  if (inflight) {
    await inflight
    if (!dirty) return
  }
  const snap = current()
  let key = snap.sync.key
  if (!key) {
    if (!hasMeaningfulProgress(snap.state)) return
    key = generateSyncKey(randomBytes)
    safeSet(SYNC_KEY, key)
    setSync({ key })
  }
  if (typeof navigator !== 'undefined' && navigator.onLine === false) {
    setSync({ status: 'offline', message: null })
    return
  }

  const run = async () => {
    const sent = current().state
    dirty = false
    setSync({ status: 'syncing', message: null })
    try {
      const res = await request('PUT', key, { state: sent }, opts.keepalive && JSON.stringify(sent).length < 60_000)
      if (res.status === 429) throw new Error('Rate limited — will retry shortly')
      if (!res.ok) throw new Error(`Sync failed (${res.status})`)
      const body = (await res.json()) as { state?: unknown }
      const merged = mergeProgress(current().state, sanitizeProgress(body.state))
      setState(merged, { markDirty: false })
      setSync({ status: 'synced', lastSyncedAt: Date.now(), message: null })
    } catch (err) {
      dirty = true
      const offline = typeof navigator !== 'undefined' && navigator.onLine === false
      setSync({ status: offline ? 'offline' : 'error', message: err instanceof Error ? err.message : 'Sync failed' })
      scheduleSync(15_000)
    }
  }

  inflight = run().finally(() => {
    inflight = null
  })
  await inflight
}

/** Fetch the server copy for this device's key and fold it in. */
async function pull(): Promise<void> {
  const key = current().sync.key
  if (!key) return
  setSync({ status: 'syncing' })
  try {
    const res = await request('GET', key)
    if (res.status === 404) {
      // Nothing remote yet (or it was deleted) — push what we have.
      dirty = true
      await syncNow()
      return
    }
    if (!res.ok) throw new Error(`Sync failed (${res.status})`)
    const body = (await res.json()) as { state?: unknown }
    const remote = sanitizeProgress(body.state)
    const merged = mergeProgress(current().state, remote)
    const localHadMore = !sameProgress(merged, remote)
    setState(merged, { markDirty: false })
    setSync({ status: 'synced', lastSyncedAt: Date.now(), message: null })
    if (localHadMore) {
      dirty = true
      scheduleSync(400)
    }
  } catch (err) {
    setSync({ status: navigator.onLine === false ? 'offline' : 'error', message: err instanceof Error ? err.message : null })
  }
}

function boot() {
  if (booted || typeof window === 'undefined') return
  booted = true
  current()

  window.addEventListener('storage', e => {
    if (e.key !== STATE_KEY && e.key !== SYNC_KEY) return
    const fresh = readLocal()
    snapshot = { state: mergeProgress(current().state, fresh.state), sync: { ...current().sync, key: fresh.sync.key } }
    emit()
  })
  window.addEventListener('online', () => {
    if (dirty) void syncNow()
    else void pull()
  })
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'hidden' && dirty) void syncNow({ keepalive: true })
    if (document.visibilityState === 'visible') void pull()
  })

  if (current().sync.key) void pull()
}

// ─── React bindings ────────────────────────────────────────────────────────────

function subscribe(listener: () => void) {
  boot()
  listeners.add(listener)
  return () => listeners.delete(listener)
}

export function useProgress(): ProgressState {
  return useSyncExternalStore(subscribe, () => current().state, () => SERVER_SNAPSHOT.state)
}

export function useSyncInfo(): SyncInfo {
  return useSyncExternalStore(subscribe, () => current().sync, () => SERVER_SNAPSHOT.sync)
}

/** True once the client store is live (false during SSR and hydration). */
export function useHydrated(): boolean {
  return useSyncExternalStore(
    subscribe,
    () => true,
    () => false,
  )
}

// ─── Actions ───────────────────────────────────────────────────────────────────

export const progress = {
  reachStep(slug: string, index: number) {
    const cur = current().state.steps[slug]
    if (cur && cur.i >= index) return
    update(d => {
      d.steps[slug] = { i: index, t: Date.now() }
    })
  },

  completeLesson(slug: string) {
    if (current().state.done[slug]) return
    update(d => {
      d.done[slug] = Date.now()
    })
  },

  markSolved(slug: string, itemId: string) {
    const id = `${slug}/${itemId}`
    if (current().state.solved[id]) return
    update(d => {
      d.solved[id] = Date.now()
    })
  },

  saveCode(slug: string, itemId: string, source: string) {
    const id = `${slug}/${itemId}`
    if (current().state.code[id]?.s === source) return
    update(d => {
      d.code[id] = { s: source.slice(0, 40_000), t: Date.now() }
    })
  },

  clearCode(slug: string, itemId: string) {
    // LWW can't express deletion, so "reset to starter" stores the empty string.
    progress.saveCode(slug, itemId, '')
  },

  /** Start fresh everywhere this key is used. */
  resetAll() {
    const now = Date.now()
    setState({ ...emptyProgress(), resetAt: now, updatedAt: now }, { markDirty: true })
  },

  /**
   * Adopt a key from another device. Local progress is merged in, so nothing
   * learned on this device is lost.
   */
  async linkKey(input: string): Promise<{ ok: true } | { ok: false; error: string }> {
    const key = normalizeSyncKey(input)
    if (!key) return { ok: false, error: 'That doesn’t look like a sync key — it should be 20 letters and digits.' }
    try {
      const res = await request('GET', key)
      if (res.status === 404) return { ok: false, error: 'No progress is stored under that key yet.' }
      if (res.status === 429) return { ok: false, error: 'Too many attempts — wait a few minutes and try again.' }
      if (!res.ok) return { ok: false, error: `Couldn’t reach the server (${res.status}).` }
      const body = (await res.json()) as { state?: unknown }
      safeSet(SYNC_KEY, key)
      setSync({ key, status: 'syncing', message: null })
      setState(mergeProgress(current().state, sanitizeProgress(body.state)), { markDirty: true })
      await syncNow()
      return { ok: true }
    } catch {
      return { ok: false, error: 'Network error — check your connection and try again.' }
    }
  },

  /** Stop syncing on this device (progress stays local; the remote copy is untouched). */
  unlink() {
    safeSet(SYNC_KEY, null)
    setSync({ key: null, status: 'local', lastSyncedAt: null, message: null })
  },

  /** Delete the remote copy and stop syncing. Local progress stays. */
  async deleteRemote(): Promise<boolean> {
    const key = current().sync.key
    if (!key) return true
    try {
      const res = await request('DELETE', key)
      if (!res.ok) return false
      progress.unlink()
      return true
    } catch {
      return false
    }
  },

  /** Make sure a key exists so the learner can copy it before progressing. */
  ensureKey(): string {
    const existing = current().sync.key
    if (existing) return existing
    const key = generateSyncKey(randomBytes)
    safeSet(SYNC_KEY, key)
    setSync({ key, status: 'syncing' })
    dirty = true
    void syncNow()
    return key
  },

  sync: () => syncNow(),
}
